/**
 * Pre-built Three.js geometries for gizmo handles and hit volumes.
 *
 * All geometries are module-level constants — created once and reused by every
 * gizmo instance (there is only one global instance anyway).
 */

import * as THREE from "three/webgpu";
import {
  ARROW_HEAD_LENGTH,
  ARROW_HEAD_RADIUS,
  ARROW_SHAFT_LENGTH,
  ARROW_SHAFT_RADIUS,
  CENTER_SPHERE_RADIUS,
  HIT_ARROW_RADIUS,
  HIT_CENTER_RADIUS,
  HIT_PLANE_SIZE,
  HIT_RING_TUBE,
  HIT_SCALE_TOUCH_RADIUS,
  OP_HANDLE_HEAD_LENGTH,
  OP_HANDLE_HEAD_RADIUS,
  OP_HANDLE_SHAFT_LENGTH,
  OP_HANDLE_SHAFT_RADIUS,
  OP_HIT_HANDLE_RADIUS,
  PLANE_QUAD_SIZE,
  POSE_CENTER_RADIUS,
  POSE_HIT_CENTER_RADIUS,
  RING_RADIUS,
  RING_TUBE,
  SCALE_BOX_SIZE,
  SCALE_BOX_TOUCH_SIZE,
  SCREEN_RING_RADIUS,
  TOUCH_HANDLE_SPHERE_RADIUS,
  TOUCH_HIT_SPHERE_RADIUS,
} from "./constants.ts";

// ---------------------------------------------------------------------------
// Translate
// ---------------------------------------------------------------------------

/** Arrow shaft — thin cylinder centered at origin, extends along +Y. */
export const ARROW_SHAFT = new THREE.CylinderGeometry(
  ARROW_SHAFT_RADIUS,
  ARROW_SHAFT_RADIUS,
  ARROW_SHAFT_LENGTH,
  8,
);
// Shift so base is at origin and tip at +Y
ARROW_SHAFT.translate(0, ARROW_SHAFT_LENGTH / 2, 0);

/** Arrow head — cone at the tip of the shaft. */
export const ARROW_HEAD = new THREE.ConeGeometry(
  ARROW_HEAD_RADIUS,
  ARROW_HEAD_LENGTH,
  12,
);
ARROW_HEAD.translate(0, ARROW_SHAFT_LENGTH + ARROW_HEAD_LENGTH / 2, 0);

/** Small quad for two-axis plane constraint. */
export const PLANE_QUAD = new THREE.PlaneGeometry(
  PLANE_QUAD_SIZE,
  PLANE_QUAD_SIZE,
);

/** Center grab sphere (free / camera-plane movement). */
export const CENTER_SPHERE = new THREE.SphereGeometry(
  CENTER_SPHERE_RADIUS,
  12,
  8,
);

// ---------------------------------------------------------------------------
// Rotate
// ---------------------------------------------------------------------------

/** Rotation ring — thin torus. */
export const ROTATE_RING = new THREE.TorusGeometry(
  RING_RADIUS,
  RING_TUBE,
  8,
  48,
);

/** Screen-aligned ring for unconstrained rotation. */
export const SCREEN_RING = new THREE.TorusGeometry(
  SCREEN_RING_RADIUS,
  RING_TUBE * 0.8,
  8,
  48,
);

// ---------------------------------------------------------------------------
// Scale
// ---------------------------------------------------------------------------

/** Scale box end cap. */
export const SCALE_BOX = new THREE.BoxGeometry(
  SCALE_BOX_SIZE,
  SCALE_BOX_SIZE,
  SCALE_BOX_SIZE,
);
SCALE_BOX.translate(0, ARROW_SHAFT_LENGTH + SCALE_BOX_SIZE / 2, 0);

// ---------------------------------------------------------------------------
// Hit volumes (invisible, enlarged for touch)
// ---------------------------------------------------------------------------

/** Fat cylinder covering both shaft and head for arrow hit testing. */
export const HIT_ARROW = new THREE.CylinderGeometry(
  HIT_ARROW_RADIUS,
  HIT_ARROW_RADIUS,
  ARROW_SHAFT_LENGTH + ARROW_HEAD_LENGTH,
  8,
);
HIT_ARROW.translate(0, (ARROW_SHAFT_LENGTH + ARROW_HEAD_LENGTH) / 2, 0);

/** Fat torus for ring hit testing. */
export const HIT_RING = new THREE.TorusGeometry(
  RING_RADIUS,
  HIT_RING_TUBE,
  8,
  24,
);

/** Large center sphere for free-movement hit testing. */
export const HIT_CENTER = new THREE.SphereGeometry(HIT_CENTER_RADIUS, 8, 6);

/** Larger quad for plane constraint hit testing. */
export const HIT_PLANE = new THREE.PlaneGeometry(
  HIT_PLANE_SIZE,
  HIT_PLANE_SIZE,
);

/** Fat cylinder covering shaft + scale box. */
export const HIT_SCALE = new THREE.CylinderGeometry(
  HIT_ARROW_RADIUS,
  HIT_ARROW_RADIUS,
  ARROW_SHAFT_LENGTH + SCALE_BOX_SIZE,
  8,
);
HIT_SCALE.translate(0, (ARROW_SHAFT_LENGTH + SCALE_BOX_SIZE) / 2, 0);

// ---------------------------------------------------------------------------
// Touch-friendly handles (coarse-pointer / mobile)
// ---------------------------------------------------------------------------

/** Large sphere at the shaft tip — replaces the arrow-head cone on touch devices. */
export const TOUCH_HANDLE_SPHERE = new THREE.SphereGeometry(
  TOUCH_HANDLE_SPHERE_RADIUS,
  14,
  10,
);
TOUCH_HANDLE_SPHERE.translate(
  0,
  ARROW_SHAFT_LENGTH + TOUCH_HANDLE_SPHERE_RADIUS,
  0,
);

/** Large hit sphere at the shaft tip for touch translate picking. */
export const HIT_TOUCH_HANDLE = new THREE.SphereGeometry(
  TOUCH_HIT_SPHERE_RADIUS,
  8,
  6,
);
HIT_TOUCH_HANDLE.translate(
  0,
  ARROW_SHAFT_LENGTH + TOUCH_HANDLE_SPHERE_RADIUS,
  0,
);

/** Larger cube end-cap for touch scale handles — cube shape distinguishes scale from translate. */
export const SCALE_BOX_TOUCH = new THREE.BoxGeometry(
  SCALE_BOX_TOUCH_SIZE,
  SCALE_BOX_TOUCH_SIZE,
  SCALE_BOX_TOUCH_SIZE,
);
SCALE_BOX_TOUCH.translate(0, ARROW_SHAFT_LENGTH + SCALE_BOX_TOUCH_SIZE / 2, 0);

/** Wide cylinder hit volume for touch scale handles. */
export const HIT_SCALE_TOUCH = new THREE.CylinderGeometry(
  HIT_SCALE_TOUCH_RADIUS,
  HIT_SCALE_TOUCH_RADIUS,
  ARROW_SHAFT_LENGTH + SCALE_BOX_TOUCH_SIZE,
  8,
);
HIT_SCALE_TOUCH.translate(
  0,
  (ARROW_SHAFT_LENGTH + SCALE_BOX_TOUCH_SIZE) / 2,
  0,
);

// ---------------------------------------------------------------------------
// Pose mode — enlarged center for free-axis joint movement
// ---------------------------------------------------------------------------

/** Larger visual sphere for pose-mode free movement. */
export const POSE_CENTER_SPHERE = new THREE.SphereGeometry(
  POSE_CENTER_RADIUS,
  16,
  12,
);

/** Larger hit volume for pose-mode free movement. */
export const POSE_HIT_CENTER = new THREE.SphereGeometry(
  POSE_HIT_CENTER_RADIUS,
  8,
  6,
);

// ---------------------------------------------------------------------------
// Operator gizmo — single normal-aligned handle (extrude / inset)
// ---------------------------------------------------------------------------

/** Operator handle shaft — thin cylinder along +Y. */
export const OP_HANDLE_SHAFT = new THREE.CylinderGeometry(
  OP_HANDLE_SHAFT_RADIUS,
  OP_HANDLE_SHAFT_RADIUS,
  OP_HANDLE_SHAFT_LENGTH,
  8,
);
OP_HANDLE_SHAFT.translate(0, OP_HANDLE_SHAFT_LENGTH / 2, 0);

/** Operator handle arrow head. */
export const OP_HANDLE_HEAD = new THREE.ConeGeometry(
  OP_HANDLE_HEAD_RADIUS,
  OP_HANDLE_HEAD_LENGTH,
  12,
);
OP_HANDLE_HEAD.translate(
  0,
  OP_HANDLE_SHAFT_LENGTH + OP_HANDLE_HEAD_LENGTH / 2,
  0,
);

/** Fat cylinder hit volume for operator handle. */
export const OP_HIT_HANDLE = new THREE.CylinderGeometry(
  OP_HIT_HANDLE_RADIUS,
  OP_HIT_HANDLE_RADIUS,
  OP_HANDLE_SHAFT_LENGTH + OP_HANDLE_HEAD_LENGTH,
  8,
);
OP_HIT_HANDLE.translate(
  0,
  (OP_HANDLE_SHAFT_LENGTH + OP_HANDLE_HEAD_LENGTH) / 2,
  0,
);
